(ns minorodata.components.pipeline-editor.sandbox-summary
  (:require
   ["lucide-react" :refer [Activity ChevronDown CircleCheck Eye FileClock History
                           LayoutList Loader Loader2 Save UndoIcon]]
   ["react" :as react]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.pipeline-editor.utils :refer [navigate-transform-tab
                                                        sandbox-actions-content]]
   [re-posh.core :as re-posh]
   [ui.button :as button]
   [ui.popover :as popover]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(def ^:private row-class "group flex min-w-0 items-stretch rounded-md")
(def ^:private row-body-class
  "flex min-w-0 flex-1 items-center gap-2 rounded-md p-2 text-sm text-left cursor-pointer transition-colors hover:bg-kleene-hover-background")
(def ^:private row-actions-class
  ["shrink-0 flex items-center gap-1 rounded-md"
   "w-0 overflow-hidden opacity-0 transition-all"
   "group-hover:w-auto group-hover:ml-1 group-hover:opacity-100"
   "focus-within:w-auto focus-within:ml-1 focus-within:opacity-100"])

(defn- subtitle-text [text]
  [:span {:class "truncate text-xs leading-3 text-kleene-dark-05"} text])

(defn- subtitle-code
  "`self-start` so the background hugs the message, not the whole row."
  [text]
  [:code {:class "self-start max-w-full truncate rounded bg-kleene-background-secondary px-1.5 py-px -my-px font-mono text-xs leading-3 text-kleene-text-secondary"}
   text])

(defn- transform-row
  "`subtitle` is a node, so a row picks its own rendering. `trailing` sits inside
   the clickable body, `actions` outside it."
  [{:keys [title title-attr subtitle on-click trailing actions]}]
  [:div {:class row-class}
   [:button {:class    row-body-class
             :title    title-attr
             :on-click on-click}
    [:div {:class "flex items-center justify-center rounded-md shrink-0 size-6 bg-transforms/20 text-transforms"}
     [:> LayoutList {:size 16}]]
    [:div {:class "min-w-0 flex flex-1 flex-col gap-1"}
     [:span {:class "truncate text-xs font-medium leading-3 text-kleene-text"}
      title]
     subtitle]
    trailing]
   actions])

(defn- section
  [{:keys [title count count-variant accessory open? on-toggle]} body]
  [:<>
   [:button {:class         "flex w-full py-2 items-center justify-between text-left group sticky -top-2 z-10 bg-kleene-background"
             :aria-expanded (boolean open?)
             :on-click      on-toggle}
    [:div {:class "flex min-w-0 items-center gap-2"}
     [typography/h4 title]
     (when (pos? count)
       [:span {:class (clsx "px-1.5 shrink-0 h-5 flex items-center justify-center rounded text-xs"
                            (if (= :error count-variant)
                              "bg-kleene-text-error/30 text-kleene-text-error"
                              "bg-kleene-text/10 text-kleene-text-secondary"))}
        count])
     accessory]
    [:> ChevronDown {:size  18
                     :class (str "shrink-0 text-kleene-text-secondary transition-transform "
                                 (when open? "rotate-180"))}]]
   (when open? body)])

(defn- empty-state [icon text]
  [:div {:class "flex flex-col items-center gap-2 py-4 text-center text-kleene-text-secondary"}
   [:> icon {:size 20 :class "opacity-60"}]
   [:span {:class "text-sm"} text]])

(defn job-status [job]
  (:sandbox-transform-execution/status job))

(defn failed-job? [job]
  (= "FAILURE" (job-status job)))

(defn queued-job? [job]
  (= "QUEUED" (job-status job)))

(defn blocked-job? [job]
  (= "BLOCKED" (job-status job)))

(defn job-transform-name [job]
  (or (:sandbox-transform-execution/name job)
      (:sandbox-transform-execution/transform-name job)
      (:sandbox-transform-execution/master-id job)))

(defn sandbox-health [jobs loading?]
  (cond
    loading?
    {:label "Loading"
     :class "border-kleene-input-border text-kleene-text-secondary"
     :dot   "bg-kleene-text-secondary"}

    (empty? jobs)
    {:label "Not run"
     :class "border-kleene-input-border text-kleene-text-secondary"
     :dot   "bg-kleene-text-secondary"}

    (some failed-job? jobs)
    {:label "Unhealthy"
     :class "border-kleene-text-error/30 text-kleene-text-error"
     :dot   "bg-kleene-text-error"}

    (some queued-job? jobs)
    {:label "Running"
     :class "border-yellow-500/30 text-yellow-600"
     :dot   "bg-yellow-500"}

    (some blocked-job? jobs)
    {:label "Blocked"
     :class "border-kleene-input-border text-kleene-text-secondary"
     :dot   "bg-kleene-text-secondary"}

    (every? #(= "SUCCESS" (job-status %)) jobs)
    {:label "Healthy"
     :class "border-kleene-text-success/30 text-kleene-text-success"
     :dot   "bg-kleene-text-success"}

    :else
    {:label "Unknown"
     :class "border-kleene-input-border text-kleene-text-secondary"
     :dot   "bg-kleene-text-secondary"}))

(defn update-type-label [type]
  (case type
    "CREATE" "Created"
    "UPDATE" "Updated"
    "DELETE" "Deleted"
    type))

(defn update-type-class [type]
  (case type
    "CREATE" "border-kleene-text-success/30 text-kleene-text-success"
    "DELETE" "border-kleene-text-error/30 text-kleene-text-error"
    "UPDATE" "border-yellow-500/30 text-yellow-600"
    "border-kleene-input-border text-kleene-text-secondary"))

(defn transform-name [{:keys [name] :as transform}]
  (or name
      (:pipeline-transform-unsaved/name transform)))

(defn transform-change-row [{:keys [type master-id transform group-id]}]
  (let [live-transform @(re-posh/subscribe
                         [:live-transform/pull
                          [:live-transform/master-id master-id]])
        group-id       (or group-id
                           (:group-id transform)
                           (:live-transform/group-id live-transform))
        group          (when group-id
                         @(re-posh/subscribe
                           [:pipeline-group/pull [:pipeline-group/id group-id]]))
        transform-name (or (transform-name transform)
                           (:live-transform/name live-transform))
        group-name     (:pipeline-group/name group)]
    (when (and transform-name group-name)
      [transform-row
       {:title      transform-name
        :title-attr "Open in a tab"
        :subtitle   [subtitle-text group-name]
        :on-click   #(navigate-transform-tab master-id group-id transform-name)
        :trailing   [:span {:class (str "shrink-0 rounded-full border px-2 py-0.5 text-xs "
                                        (update-type-class type))}
                     (update-type-label type)]}])))

(defn failing-job-row [job]
  (let [master-id         (:sandbox-transform-execution/master-id job)
        sandbox-transform @(re-posh/subscribe
                            [:sandbox-transform/pull
                             [:sandbox-transform/master-id master-id]])
        group-id          (:sandbox-transform/group-id sandbox-transform)
        transform-name    (or (:sandbox-transform/name sandbox-transform)
                              (job-transform-name job))
        error-message     (not-empty (:sandbox-transform-execution/error job))]
    [tooltip/tooltip-root
     [tooltip/tooltip-trigger {:as-child true}
      [:div
       [transform-row
        {:title    transform-name
         :subtitle (if error-message
                     [subtitle-code error-message]
                     [subtitle-text "No error message available"])
         :on-click #(navigate-transform-tab master-id group-id transform-name)}]]]

     (when error-message
       [tooltip/tooltip-content
        {:class "text-xs overflow-auto bg-kleene-dark-01 text-kleene-secondary-05 max-w-4xl w-full border-none"}
        [:div
         [:pre {:class "whitespace-pre-wrap select-text"}
          [:code error-message]]]])]))

(defn unsaved-transform-row [{:keys [transform transform-name group-name group-id]}]
  (let [{:pipeline-transform-unsaved/keys [id]} transform]
    [transform-row
     {:title      transform-name
      :title-attr "Open in a tab"
      :subtitle   [subtitle-text group-name]
      :on-click   #(navigate-transform-tab id group-id transform-name)
      :actions
      [:div {:class (clsx row-actions-class)}
       [tooltip/tooltip-root
        [tooltip/tooltip-trigger
         (let [saving? @(re-posh/subscribe
                         [:mold-http/loading-id?
                          :sandbox.api/update-sandbox-transform
                          (keyword (str "update-sandbox-transform-" id))])]
           [button/button {:size      :icon
                           :icon      Save
                           :variant   :empty-transparent
                           :component :div
                           :disabled  saving?
                           :loading   saving?
                           :on-click  #(dispatch [:sandbox.events/update-transform {:id id}])}])]
        [tooltip/tooltip-content "Save changes"]]

       [tooltip/tooltip-root
        [tooltip/tooltip-trigger
         [button/button {:size      :icon
                         :icon      UndoIcon
                         :variant   :error-empty
                         :component :div
                         :on-click  #(dispatch [:pipeline-editor.events/purge-local-transform id])}]]
        [tooltip/tooltip-content "Undo changes"]]]}]))

(defn unsaved-transforms-list [{:keys [unsaved-transforms]}]
  (let [visible-transforms
        (keep
         (fn [{:pipeline-transform-unsaved/keys [id name group-id] :as transform}]
           (let [sandbox-transform @(re-posh/subscribe
                                     [:sandbox-transform/pull
                                      [:sandbox-transform/master-id id]])
                 group-id          (or group-id
                                       (:sandbox-transform/group-id sandbox-transform))
                 group             (when group-id
                                     @(re-posh/subscribe
                                       [:pipeline-group/pull
                                        [:pipeline-group/id group-id]]))
                 transform-name    (or name
                                       (:pipeline-transform-unsaved/name transform)
                                       (:sandbox-transform/name sandbox-transform))
                 group-name        (:pipeline-group/name group)]
             (when (and transform-name group-name)
               {:transform      transform
                :transform-name transform-name
                :group-name     group-name
                :group-id       group-id})))
         unsaved-transforms)]
    (cond
      (not-empty visible-transforms)
      [:div {:class "flex flex-col gap-0.5"}
       (for [{:keys [transform] :as props} visible-transforms]
         ^{:key (:pipeline-transform-unsaved/id transform)}
         [unsaved-transform-row props])]

      (not-empty unsaved-transforms)
      [empty-state FileClock "Loading changes..."]

      :else
      [empty-state CircleCheck "No unsaved changes"])))


(defn sandbox-summary' [{:keys [updates jobs statuses-loading? preview-loading?
                                 versions unsaved-transforms]}]
  (react/useEffect
   (fn []
     (dispatch [:sandbox.api/fetch-live-transforms])
     js/undefined)
   #js [])

  (let [[status-open? set-status-open]   (react/useState true)
        [unsaved-open? set-unsaved-open] (react/useState true)
        [changes-open? set-changes-open] (react/useState true)
        in-flight?                       (or statuses-loading? preview-loading?)
        status-loading?                  (and in-flight? (empty? jobs))
        refreshing?                      (and in-flight? (not status-loading?))
        update-items                     (:sandbox-updates/updates updates)
        failing-jobs                     (filter failed-job? jobs)
        no-changes?                      (empty? update-items)
        health                           (sandbox-health jobs status-loading?)]
    ;; One scroll area: sections scroll together, the actions row stays pinned.
    [:div {:class "bg-kleene-background h-full grid grid-rows-[1fr_auto] overflow-hidden"}
     [:div {:class "min-h-0 overflow-y-auto px-4 py-2 flex flex-col gap-3"}
      [section {:title         "Status"
                :count         (count failing-jobs)
                :count-variant :error
                :accessory     [:div {:class "flex shrink-0 items-center gap-2"}
                                (when refreshing?
                                  [:> Loader2 {:size  14
                                               :class "shrink-0 animate-spin text-kleene-text-secondary"}])
                                [:span {:class (str "inline-flex shrink-0 items-center gap-2 rounded-full border px-2 py-0.5 text-sm "
                                                    (:class health))}
                                 [:span {:class (str "h-2 w-2 rounded-full " (:dot health))}]
                                 (:label health)]]
                :open?         status-open?
                :on-toggle     #(set-status-open (not status-open?))}
       (cond
         status-loading?
         [empty-state Loader "Loading transform status..."]

         (empty? jobs)
         [empty-state Activity "Nothing has run in this sandbox yet."]

         (not-empty failing-jobs)
         [:div {:class "flex flex-col gap-0.5"}
          (for [[idx job] (map-indexed vector failing-jobs)]
            ^{:key (or (:sandbox-transform-execution/master-id job) idx)}
            [failing-job-row job])]

         :else
         [empty-state CircleCheck "No failing transforms"])]

      [section {:title        "Unsaved changes"
                :count        (count unsaved-transforms)
                :open?        unsaved-open?
                :on-toggle    #(set-unsaved-open (not unsaved-open?))}
       [unsaved-transforms-list {:unsaved-transforms unsaved-transforms}]]

      [section {:title        "Pending changes"
                :count        (count update-items)
                :open?        changes-open?
                :on-toggle    #(set-changes-open (not changes-open?))}
       (if no-changes?
         [empty-state CircleCheck "No pending changes"]
         [:div {:class "flex flex-col gap-0.5"}
          (for [[idx {:keys [master-id] :as update}]
                (map-indexed vector update-items)]
            ^{:key (or master-id idx)}
            [transform-change-row update])])]]

     [:div {:class "shrink-0 border-t bg-kleene-background px-6 py-4"}
      [:div {:class "flex w-full gap-3"}
       [popover/popover-root
        [popover/popover-trigger {:as-child true}
         [:> button/button-fr
          {:variant       :secondary
           :class         "w-full"
           :wrapper-class "w-full"}
          [:div {:class "flex w-full items-center justify-center gap-2 whitespace-nowrap"}
           [:> History {:size 18 :class "shrink-0"}]
           [:span {:class "shrink-0"} "Stored versions"]]]]

        [popover/popover-content {:class "w-xl max-h-96 overflow-y-auto p-0"
                                  :align "end"}
         [:f> sandbox-actions-content {:versions versions}]]]

       [button/button
        {:disabled      no-changes?
         :variant       :primary
         :class         "w-full"
         :wrapper-class "w-full"
         :on-click      #(dispatch [:app.events/navigate-query {:popup "review"}])}
        [:div {:class "flex w-full items-center justify-center gap-2 whitespace-nowrap"}
         [:> Eye {:size 18 :class "shrink-0"}]
         [:span {:class "shrink-0"} "Review"]]]]]]))

(defn sandbox-summary []
  (let [updates            @(re-posh/subscribe [:sandbox-updates/pull])
        jobs               @(re-posh/subscribe [:sandbox-transform-execution/pull-many])
        statuses-loading?  @(re-posh/subscribe [:mold-http/loading?
                                                :sandbox.api/fetch-execution-statuses])
        preview-loading?   @(re-posh/subscribe [:mold-http/loading?
                                                :sandbox.api/preview-sandbox-sql])
        versions           @(re-posh/subscribe [:sandbox.subs/versions])
        unsaved-transforms @(re-posh/subscribe [:pipeline-transform-unsaved/pull-many])]
    [:f> sandbox-summary' {:updates            updates
                            :jobs               jobs
                            :statuses-loading?  statuses-loading?
                            :preview-loading?   preview-loading?
                            :versions           versions
                            :unsaved-transforms unsaved-transforms}]))
